import {
  BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Users } from 'lucide-react';
import type { ReviewerLoadRow } from '@/hooks/useTeamMonitoring';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ReviewerLoadChartProps {
  data: ReviewerLoadRow[];
  loading?: boolean;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function ChartSkeleton() {
  return (
    <div role="status" aria-label="Loading reviewer load chart" className="h-[200px] animate-pulse rounded bg-muted" aria-hidden="true" />
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Reviewer Load bar chart — FR-083.
 * Horizontal bar chart showing number of completed reviews per reviewer.
 * Admin-only (rendered inside admin-gated TeamMonitoringTab).
 *
 * WCAG: chart title + desc for screen readers.
 */
export function ReviewerLoadChart({ data, loading }: ReviewerLoadChartProps) {
  return (
    <section
      className="rounded-lg border border-border bg-card p-4 space-y-3"
      aria-label="Reviews per reviewer"
    >
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">Reviews per Reviewer</h3>
      </div>

      {loading ? (
        <ChartSkeleton />
      ) : data.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-8 text-center">No review data yet.</p>
      ) : (
        <>
          {/* Screen-reader description */}
          <p className="sr-only">
            Bar chart showing review counts per reviewer.
            {data.map((d) => `${d.reviewer_name}: ${d.review_count} reviews.`).join(' ')}
          </p>
          <div className="h-[200px]" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="reviewer_name"
                  type="category"
                  width={100}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [value, 'Reviews']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="review_count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
}
